import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Button, Typography, Box, Tooltip, styled, Alert, Checkbox, FormControlLabel, IconButton, Popover, Divider } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PulseLoader from '@/components/PulseLoader';
import { Download } from "lucide-react";
import MediaQuery from 'react-responsive';
import {
  fetchLiveParticipants,
  fetchSessionAttendances,
  normalizeSessionProvider,
} from '../../../liveSession/api';
import { getViewSessionsPath } from './sessionUtils/sessionUtils';

// Type definitions
interface LogEntry {
  session_start_time: string;
  session_end_time: string | null;
  duration_minutes: number;
}

interface Participant {
  participant_id: string;
  display_name: string;
  email: string;
  student_id: string;
  session_id: string;
  attendedTime: number;
  log: LogEntry[];
  course_name?: string;
  batch_name?: string;
  college_name?: string;
  branch?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  participants: Participant[];
  session_id: string;
}

const LiveAttendance: React.FC = () => {
  const { sessionName } = useParams<{ sessionName: string }>();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
    student_id: false,
    display_name: true,
    college_name: true,
    branch: true,
    course_name: true,
    batch_name: true,
    email: false,
    attendedTime: true,
    log: true,
  };
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({ ...DEFAULT_COLUMN_VISIBILITY });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const isSettingsOpen = Boolean(settingsAnchorEl);
  const handleOpenSettings = (event: React.MouseEvent<HTMLElement>) => setSettingsAnchorEl(event.currentTarget);
  const handleCloseSettings = () => setSettingsAnchorEl(null);
  const handleResetVisibility = () => setColumnVisibility({ ...DEFAULT_COLUMN_VISIBILITY });

  // Get session data from sessionStorage or API
  const [sessionData, setSessionData] = useState<any>(null);

  // Get filter parameters and session ID from URL
  const urlParams = new URLSearchParams(window.location.search);
  const selectedTrack = urlParams.get('track') || '';
  const selectedCourse = urlParams.get('course') || '';
  const selectedBatch = urlParams.get('batch') || '';
  const sessionId = urlParams.get('sessionId') || '';
  const providerFromUrl = urlParams.get('provider') || '';

  const handleSelectAllChange = (): void => {
    if (selectAll) {
      setSelectedRows([]); // Deselect all if already selected
    } else {
      setSelectedRows(participants.map(participant => participant.student_id)); // Select all students
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRowChange = (student_id: string): void => {
    const newSelectedRows = selectedRows.includes(student_id)
      ? selectedRows.filter(id => id !== student_id)
      : [...selectedRows, student_id];
    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.length === participants.length); // Update select all checkbox
  };

  const CustomTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    "& .MuiTooltip-tooltip": {
      backgroundColor: "white",
      color: "black",
      boxShadow: theme.shadows[1],
      fontSize: 13,
      border: "1px solid #dadde9",
      "& .MuiTooltip-arrow": {
        color: "white",
      },
    },
  }));

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        const providerHint = providerFromUrl
          ? normalizeSessionProvider(providerFromUrl)
          : undefined;
        const sessions = await fetchSessionAttendances({
          track_id: selectedTrack,
          course_id: selectedCourse,
          batch_id: selectedBatch,
          session_provider: providerHint,
        });

        const session = (Array.isArray(sessions) ? sessions : []).find(
          (s: any) => s.id === parseInt(sessionId || '0')
        );

        if (!session) {
          setError(`Session not found with ID: ${sessionId}`);
          setIsLoading(false);
          return;
        }

        setSessionData(session);
      } catch (error) {
        setError("Failed to load session data");
        setIsLoading(false);
      }
    };

    if (sessionId) {
      fetchSessionData();
    } else {
      setError('No session ID provided');
      setIsLoading(false);
    }
  }, [sessionId, selectedTrack, selectedCourse, selectedBatch, providerFromUrl]);

  const loadLiveParticipants = async () => {
    if (!sessionData) return;

    try {
      setRefreshing(true);
      const email =
        sessionData.created_by_email ||
        sessionStorage.getItem('Email') ||
        '';
      const data: ApiResponse = await fetchLiveParticipants({
        email,
        meeting_link: sessionData.meetlink,
        instructor_name: sessionData.created_by,
        session_id: parseInt(sessionId || '0'),
      });

      if (data.success) {
        setParticipants(data.participants || []);
        setError('');
      } else {
        setError(data.message || "Failed to fetch live participants");
      }
    } catch (error) {
      console.error("Error fetching live participants:", error);
      setError("Failed to fetch live participants");
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (sessionData) {
      loadLiveParticipants();

      // Set up auto-refresh every 30 seconds
      const interval = setInterval(loadLiveParticipants, 30000);
      return () => clearInterval(interval);
    }
  }, [sessionData]);

  const handleRefresh = () => {
    loadLiveParticipants();
  };

  const handleBack = () => {
    const currentPath = window.location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin');
    const prefix = isAdminRoute ? '/admin' : '/trainer';
    navigate(getViewSessionsPath(prefix));
  };

  const filteredParticipants = useMemo(() => {
    return participants.filter(
      (participant) => participant.student_id !== "0000"
    );
  }, [participants]);

  const attendanceSummary = useMemo(() => {
    const attendedCount = filteredParticipants.filter((p) => p.attendedTime > 0).length;
    const totalInvited = filteredParticipants.length;
    return `${attendedCount}/${totalInvited} attended`;
  }, [filteredParticipants]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "student_id",
        header: "ID",
        size: 50,
        filterFn: "contains" as const,
      },
      {
        accessorKey: "display_name",
        header: "Student Name",
        filterFn: "contains" as const,
      },
      {
        accessorKey: "college_name",
        header: "College",
        filterFn: "contains" as const,
        size: 50,
      },
      {
        accessorKey: "branch",
        header: "Branch",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "course_name",
        header: "Course",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "batch_name",
        header: "Batch",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "email",
        header: "Email",
        filterFn: "contains" as const,
      },
      {
        accessorKey: "attendedTime",
        header: "Attended Time (min)",
        filterFn: "greaterThan" as const,
        Cell: ({ cell }: { cell: { getValue: () => number } }) => (
          <Typography
            sx={{
              color: cell.getValue() > 0 ? "green" : "red",
              fontWeight: cell.getValue() > 0 ? "normal" : "bold",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "log",
        header: "Session Logs",
        Cell: ({ row }: { row: { original: Participant } }) => {
          const participant = row.original;
          return (
            <CustomTooltip
              title={
                <div style={{ padding: '10px', backgroundColor: '#f1f1f1', borderRadius: '5px' }}>
                  <h4>Logs for {participant.display_name || participant.student_id}</h4>
                  <ul>
                    {participant.log?.map((logEntry, index) => (
                      <li key={index}>
                        <strong>Start Time:</strong> {new Date(logEntry.session_start_time).toLocaleString()} <br />
                        <strong>End Time:</strong> {logEntry.session_end_time ? new Date(logEntry.session_end_time).toLocaleString() : '--'} <br />
                        <strong>Duration:</strong> {Math.round(logEntry.duration_minutes)} minutes
                      </li>
                    ))}
                  </ul>
                </div>
              }
              arrow
              placement="top"
            >
              <div
                style={{
                  color: participant.attendedTime > 0 ? "green" : "red",
                  fontWeight: participant.attendedTime > 0 ? "normal" : "bold",
                  cursor: "pointer",
                }}
              >
                View Logs ({participant.log?.length || 0})
              </div>
            </CustomTooltip>
          );
        },
      },
    ];
  }, []);

  const table = useMaterialReactTable({
    columns,
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
    enableColumnFilters: false,
    positionToolbarAlertBanner: "bottom" as const,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      showColumnFilters: false,
      density: "compact" as const,
      columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY },
    },
    muiTopToolbarProps: {
      sx: { borderBottom: '2px solid rgba(0,0,0,0.2)' },
    },
    data: filteredParticipants,
    enableRowSelection: true,
    enableHiding: true,
    enablePagination: true,
    onRowSelectionChange: (updaterOrValue) => {
      const newSelection = typeof updaterOrValue === 'function' 
        ? updaterOrValue(selectedRows.reduce((acc, id) => ({ ...acc, [id]: true }), {}))
        : updaterOrValue;
      
      const selectedIds = Object.keys(newSelection).filter(id => newSelection[id]);
      setSelectedRows(selectedIds);
      setSelectAll(selectedIds.length === filteredParticipants.length && filteredParticipants.length > 0);
    },
    state: {
      rowSelection: selectedRows.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (row: Participant) => row.student_id,
    muiTableContainerProps: {
      sx: {
        height: "calc(100vh - 330px)",
        overflow: 'auto'
      }
    },
    muiPaginationProps: {
      rowsPerPageOptions: [15, 20, 25, 30, 50],
      showFirstButton: true,
      showLastButton: true,
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const selectedRowsCount = selectedRows.length;
      return (
        <Box
          sx={{
            display: "flex",
            gap: "0.5rem",
            p: "0.5rem",
            alignItems: "center",
            width: '100%',
            position: 'relative'
          }}
        >
          <Button
            color="primary"
            disabled={selectedRowsCount === 0}
            onClick={handleExport}
            variant="contained"
            startIcon={<Download size={20} />}
            size="small"
          >
            Export ({selectedRowsCount})
          </Button>
          <Box sx={{ position: 'absolute', right: 0, top: 6 }}>
            <IconButton size="small" onClick={handleOpenSettings} title="Column Visibility">
              <SettingsIcon />
            </IconButton>
          </Box>
            <Popover
              open={isSettingsOpen}
              anchorEl={settingsAnchorEl}
              onClose={handleCloseSettings}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, width: 280 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Column Visibility</Typography>
                  <Button size="small" onClick={handleResetVisibility}>Reset</Button>
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.display_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, display_name: e.target.checked }))} />} label="Student Name" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.student_id} onChange={(e) => setColumnVisibility((p) => ({ ...p, student_id: e.target.checked }))} />} label="Student ID" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.college_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, college_name: e.target.checked }))} />} label="College" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.branch} onChange={(e) => setColumnVisibility((p) => ({ ...p, branch: e.target.checked }))} />} label="Branch" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.course_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, course_name: e.target.checked }))} />} label="Course" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.batch_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, batch_name: e.target.checked }))} />} label="Batch" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.email} onChange={(e) => setColumnVisibility((p) => ({ ...p, email: e.target.checked }))} />} label="Email" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.attendedTime} onChange={(e) => setColumnVisibility((p) => ({ ...p, attendedTime: e.target.checked }))} />} label="Attended Time (min)" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.log} onChange={(e) => setColumnVisibility((p) => ({ ...p, log: e.target.checked }))} />} label="Session Logs" />
                </Box>
              </Box>
            </Popover>
        </Box>
      );
    },
  });

  const handleExport = (): void => {
    // Get data to export based on whether we're on mobile or desktop
    let dataToExport: Participant[];
    
    // On desktop, use MaterialReactTable's filtering; on mobile, use participants array directly
    if (typeof MediaQuery !== 'undefined' && table.getFilteredRowModel) {
      const filteredRows = table.getFilteredRowModel().rows;
      dataToExport = selectedRows.length
        ? filteredRows
            .filter((row) => selectedRows.includes(row.original.student_id))
            .map((row) => row.original)
        : filteredRows.map((row) => row.original);
    } else {
      dataToExport = selectedRows.length
        ? participants.filter((participant) => selectedRows.includes(participant.student_id))
        : participants;
    }

    if (dataToExport.length === 0) {
      alert("No rows selected to export.");
      return;
    }

    // Create CSV content without Participant ID and Session ID
    const csvContent = [
      [
        "Student ID", 
        "Student Name",
        "Email",
        "Attended Time (min)",
        "Course",
        "Batch", 
        "College",
        "Branch",
        "Log Count"
      ],
      ...dataToExport.map((participant) => [
        participant.student_id,
        participant.display_name,
        participant.email,
        participant.attendedTime,
        participant.course_name || '',
        participant.batch_name || '',
        participant.college_name || '',
        participant.branch || '',
        participant.log?.length || 0
      ]),
    ]
      .map((row) =>
        row
          .map((cell) =>
            // Properly escape cells that contain commas or quotes
            typeof cell === "string"
              ? `"${cell.replace(/"/g, '""')}"`
              : `"${cell}"`
          )
          .join(",")
      )
      .join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    link.setAttribute("href", url);
    link.setAttribute("download", `live_participants_${sessionId || 'unknown'}_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="my-2 me-2 rounded-2 bg-white" style={{ height: `calc(100vh - 90px)`,display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <PulseLoader color="#1976d2" />
      </div>
    );
  }
  
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <div className="my-2 me-2 rounded-2" style={{ backgroundColor: "white", height: `calc(100vh - 95px)`,overflowY: "auto" }}>
      {/* Session Header */}
      {sessionData && (
        <Box sx={{ 
          p: 2, 
          borderBottom: '2px solid #e0e0e0', 
          backgroundColor: '#f8f9fa',
          borderRadius: '8px 8px 0 0'
        }}>
          <Typography variant="subtitle1" sx={{ color: '#666', fontSize: '16px' }}>
            Trainer: {sessionData.conductedby}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#888', fontSize: '14px', mt: 0.5 }}>
            Started: {sessionData.Start_Time} ({Math.floor((new Date().getTime() - new Date(`${sessionData.Date}T${sessionData.Start_Time}`).getTime()) / (1000 * 60))} mins)
          </Typography>
        </Box>
      )}
      
      <MediaQuery minWidth={1137}>
      <Box>
      <MaterialReactTable table={table} />
    </Box>
    </MediaQuery>
    <MediaQuery maxWidth={1136}>
 <Box sx={{ p: 1, backgroundColor: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)', borderRadius: 1 }}>
   <div style={{height:'35px',maxHeight:'35px'}}>
   <div className="d-flex justify-content-between ">
     <h5>Live Attendance</h5>
     <Button
            className="btn btn-sm btn-primary"
            color="primary"
            disabled={selectedRows.length === 0}
            onClick={handleExport}
            variant="contained"
            startIcon={<Download size={20} />}
            size="small"
          >
            Export
          </Button>
   </div>

   </div>
   
   <div style={{ height: 'calc(100vh - 290px)', overflow: 'auto' }}>
     <table style={{ width: '100%', borderCollapse: 'collapse' }}>
       <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f1f1', zIndex: 10 }}>
         <tr>
           <th>
             <input
               type="checkbox"
               checked={selectAll}
               onChange={handleSelectAllChange}
             />
           </th>
           <th>ID</th>
           <th>Name</th>
           <th>College-Branch</th>
           <th>Course-Batch</th>
           <th>Email</th>
           <th>Attended Time</th>
           <th>Logs</th>
         </tr>
       </thead>
       <tbody>
         {participants.map((participant) => (
           <tr key={participant.student_id}>
             <td>
               <input
                 type="checkbox"
                 checked={selectedRows.includes(participant.student_id)}
                 onChange={() => handleSelectRowChange(participant.student_id)}
               />
             </td>
             <td>{participant.student_id}</td>
             <td>{participant.display_name}</td>
             <td>{participant.college_name || ''}-{participant.branch || ''}</td>
             <td>{participant.course_name || ''}-{participant.batch_name || ''}</td>
             <td>{participant.email}</td>
             <td style={{ color: participant.attendedTime > 0 ? 'green' : 'red' }}>
               {participant.attendedTime} min
             </td>
             <td>
               <CustomTooltip
                 title={
                   <div style={{ padding: '10px', backgroundColor: '#f1f1f1', borderRadius: '5px' }}>
                     <h4>Logs for {participant.display_name || participant.student_id}</h4>
                     <ul>
                       {participant.log?.map((logEntry, index) => (
                         <li key={index}>
                           <strong>Start Time:</strong> {new Date(logEntry.session_start_time).toLocaleString()} <br />
                           <strong>End Time:</strong> {logEntry.session_end_time ? new Date(logEntry.session_end_time).toLocaleString() : '--'} <br />
                           <strong>Duration:</strong> {logEntry.duration_minutes} minutes
                         </li>
                       ))}
                     </ul>
                   </div>
                 }
                 arrow
                 placement="top"
               >
                 <span style={{ cursor: 'pointer' }}>
                   View Logs ({participant.log?.length || 0})
                 </span>
               </CustomTooltip>
             </td>
           </tr>
         ))}
       </tbody>
     </table>
   </div>
 </Box>
</MediaQuery>
    </div>
  );
};

export default LiveAttendance;
